/**
 * Liga o cartão flutuante ao relógio COMPARTILHADO da encenação
 * (`utils/encenacao-do-plano.js`, §5.1). Quem ALIMENTA o relógio é o
 * `messages-painel-do-chat-mixin.js` — o `messages.vue` está montado o tempo
 * inteiro, enquanto o cartão só monta depois da medida da faixa (no 1º turno de
 * uma página nova ele chegava tarde e aquele turno não era encenado). Este
 * mixin é o consumidor, e re-sincroniza o plano que JÁ está na fila (mantém
 * `total`/`alvo` em dia quando o cartão monta no meio dela).
 *
 * ⚠️ Só o plano do turno EM VOO nesta sessão é encenado (`turnoEmVoo`, o
 * `chatLoading`): o `plano` também chega de HISTÓRICO (`getMessages` ao abrir
 * uma conversa antiga) e do replay do run terminal (`apiChat` drena o backlog
 * na abertura), e ali a tela já tem o estado final — encenar de novo fazia uma
 * conversa pronta reanimar "4 de 7 etapas" e esconder o texto por ~2s.
 *
 * Só a EXIBIÇÃO é ritmada: texto, entregas e o turno seguem no tempo deles.
 * Consome `progressoDoPainel` e `turnoEmVoo` (dados-do-painel-mixin) e expõe
 * `progressoExibido`.
 */
import {
  alvoDaEncenacao,
  assinaturaDePlano,
  passosEncenados,
} from '@modules/aawz-intelligence-v2/utils/encenacao-do-progresso'
import { encenacaoDoPlano, sincronizarEncenacao } from '@modules/aawz-intelligence-v2/utils/encenacao-do-plano'

export default {
  data() {
    return {
      // As entregas que já estavam na lista quando a encenação começou.
      entregasNoInicioDaEncenacao: [],
    }
  },
  computed: {
    /**
     * ★ [MUST-4 do review do PR #2319] A guarda de ASSINATURA é a primeira
     * linha, como no `passosRevelados` abaixo e em `chat-message-plano.vue::
     * itensExibidos`. O relógio é COMPARTILHADO (singleton de módulo): sem
     * comparar a assinatura, disparar um turno na conversa A e trocar para a B
     * dentro da janela de encenação (até `TETO_DA_ENCENACAO_MS`, 2s) fazia o
     * cartão da B se dar por encenando o plano da A — e
     * `entregasNoInicioDaEncenacao` congelava as identidades de A, filtrando as
     * entregas de B contra elas (a seção "Entregas" sumia e voltava).
     */
    encenandoProgresso() {
      if (encenacaoDoPlano.assinatura !== this.assinaturaDoProgresso) return false
      return encenacaoDoPlano.revelados < encenacaoDoPlano.alvo
    },
    assinaturaDoProgresso() {
      return assinaturaDePlano((this.progressoDoPainel || []).map((passo) => passo.titulo))
    },
    passosRevelados() {
      // lê o singleton reativo: `encenacaoDoPlano.revelados` entra na dependência
      return encenacaoDoPlano.assinatura === this.assinaturaDoProgresso
        ? encenacaoDoPlano.revelados
        : (this.progressoDoPainel || []).length
    },
    progressoExibido() {
      return passosEncenados(this.progressoDoPainel, this.passosRevelados)
    },
  },
  watch: {
    encenandoProgresso: {
      immediate: true,
      handler(encenando) {
        if (!encenando) return
        this.entregasNoInicioDaEncenacao = (this.entregasDoPainel || []).map((linha) => linha.identidade)
      },
    },
    progressoDoPainel: {
      immediate: true,
      handler(passos) {
        const lista = passos || []
        if (!lista.length) return
        // Plano NOVO só entra na fila com turno em voo (histórico/replay
        // assenta) — e quem estreia um plano é o `messages.vue`.
        const jaNaFila = this.assinaturaDoProgresso === encenacaoDoPlano.assinatura
        if (!jaNaFila && !this.turnoEmVoo) return
        sincronizarEncenacao({
          assinatura: this.assinaturaDoProgresso,
          total: lista.length,
          alvo: alvoDaEncenacao(lista),
        })
      },
    },
  },
}
